import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardMedia,
  IconButton,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { db, storage } from "../firebase";
import { ref, push, onValue, set, remove } from "firebase/database";
import { ref as sRef, deleteObject } from "firebase/storage";

import { uploadToStorage } from "../utils/uploadToStorage";

export default function ManageHomepageBanner() {
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const [banners, setBanners] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const bannerRef = ref(db, "banners");

    return onValue(bannerRef, (snap) => {
      const data = snap.val() || {};
      const list = Object.values(data);
      setBanners(list);
    });
  }, []);

  const uploadBanner = async () => {
    if (!link.trim()) {
      alert("Enter redirect link");
      return;
    }

    // 🔁 Update ONLY link
    if (selected && !file) {
      await set(ref(db, `banners/${selected.bannerId}`), {
        ...selected,
        redirectLink: link.trim(),
      });
      resetForm();
      return;
    }

    if (!file) {
      alert("Select image first");
      return;
    }

    setUploading(true);

    const bannerId = selected?.bannerId || push(ref(db, "banners")).key;
    if (!bannerId) return;

    // ⭐️ THIS is the important part (SDK upload)
    const imageUrl = await uploadToStorage(
      `banners/${bannerId}.jpg`,
      file,
      setProgress
    );

    const model = {
      bannerId,
      imageUrl,
      redirectLink: link.trim(),
      createdAt: Date.now(),
    };

    await set(ref(db, `banners/${bannerId}`), model);

    // delete old image if replacing
    if (selected) {
      deleteObject(sRef(storage, `banners/${selected.bannerId}.jpg`)).catch(() => {});
    }

    resetForm();
  };

  const deleteBanner = async (b) => {
    if (!window.confirm("Delete this banner?")) return;

    await deleteObject(sRef(storage, `banners/${b.bannerId}.jpg`)).catch(() => {});
    await remove(ref(db, `banners/${b.bannerId}`));
  };

  const resetForm = () => {
    setSelected(null);
    setFile(null);
    setLink("");
    setProgress(0);
    setUploading(false);
  };

  const startEdit = (b) => {
    setSelected(b);
    setLink(b.redirectLink);
    setFile(null);
  };

  return (
    <Box>
      <Typography variant="h6">Manage Homepage Banners</Typography>

      <TextField
        fullWidth
        label="Redirect Link"
        sx={{ mt: 2 }}
        value={link}
        onChange={(e) => setLink(e.target.value)}
      />

      <Button component="label" variant="outlined" fullWidth sx={{ mt: 2 }}>
        Select Banner Image
        <input
          type="file"
          hidden
          accept="image/*"
          onChange={(e) => setFile(e.target.files[0])}
        />
      </Button>

      {file && <Typography mt={1}>Selected: {file.name}</Typography>}

      <Button
        fullWidth
        sx={{ mt: 2 }}
        variant="contained"
        disabled={uploading}
        onClick={uploadBanner}
      >
        {selected ? "Update Banner" : "Upload Banner"}
      </Button>

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography align="center">{progress}%</Typography>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        {banners.map((b) => (
          <Card key={b.bannerId} sx={{ mb: 2, position: "relative" }}>
            <CardMedia component="img" height="160" image={b.imageUrl} />
            <IconButton sx={{ position: "absolute", top: 5, right: 45 }} onClick={() => startEdit(b)}>
              ✏️
            </IconButton>
            <IconButton sx={{ position: "absolute", top: 5, right: 5 }} onClick={() => deleteBanner(b)}>
              <DeleteIcon />
            </IconButton>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
